/**
 * Artboard model for the canvas editor.
 *
 * A canvas root should be a real "artboard" FRAME (the page), not a loose
 * background rectangle. The artboard owns the background fill, and all content
 * lives inside it. This is what makes sizing behave like Figma:
 *   - email artboards are fixed-width + auto-height (VERTICAL auto-layout, HUG),
 *     so they grow/shrink with content (the page "hugs"),
 *   - social/custom artboards are a fixed size.
 *
 * `createArtboardDoc` builds a fresh artboard doc; `ensureArtboardRoot`
 * upgrades legacy "loose bg rectangle" docs to the artboard model on open.
 */
import type { SceneDocument, SceneNode, Paint } from '@/lib/scene-document';
import { generateNodeId } from './canvaskit-mutations';

const WHITE: Paint = { type: 'SOLID', color: '#FFFFFF' };

/**
 * The authored link-zone group, which lives at the scene root beside the
 * artboard rather than inside it. Matches on either signal: the Figma importer
 * emits a first-class `LINK_ZONE_GROUP`, while zones added in the editor are a
 * plain GROUP tagged `slotRole: 'link-zone'` (what the compiler keys on).
 */
function isLinkZoneRoot(node: SceneNode | undefined): boolean {
  return node?.type === 'LINK_ZONE_GROUP' || node?.slotRole === 'link-zone';
}

/** Default floor height for an empty hugging (email) artboard. */
export const EMAIL_MIN_HEIGHT = 800;
export const EMAIL_WIDTH = 600;

export interface ArtboardFormat {
  /** 'email' = fixed width, hugging (auto) height; 'fixed' = fixed both. */
  kind: 'email' | 'fixed';
  width: number;
  height: number;
  /** Floor height for hugging artboards (kind 'email'). */
  minHeight?: number;
  label?: string;
}

/** Quick-pick formats for the "create canvas" / "add frame" surfaces. */
export const ARTBOARD_FORMATS: Array<{ id: string } & ArtboardFormat> = [
  { id: 'email', label: 'Email', kind: 'email', width: EMAIL_WIDTH, height: EMAIL_MIN_HEIGHT, minHeight: EMAIL_MIN_HEIGHT },
  { id: 'square', label: 'Square (1080)', kind: 'fixed', width: 1080, height: 1080 },
  { id: 'story', label: 'Story (1080×1920)', kind: 'fixed', width: 1080, height: 1920 },
  { id: 'landscape', label: 'Landscape (1200×628)', kind: 'fixed', width: 1200, height: 628 },
  { id: 'custom', label: 'Custom', kind: 'fixed', width: 800, height: 600 },
];

/**
 * Build an artboard FRAME node. Email artboards stack their children vertically
 * and hug their height (down to `minHeight`); fixed artboards keep their size.
 */
export function makeArtboardFrame(
  format: ArtboardFormat,
  id: string,
  children: string[] = [],
): SceneNode {
  const base: SceneNode = {
    id,
    type: 'FRAME',
    name: format.kind === 'email' ? 'Email' : 'Artboard',
    parentId: null,
    x: 0,
    y: 0,
    width: format.width,
    height: format.height,
    fills: [WHITE],
    clipContent: true,
    children,
    pluginData: { isArtboard: true },
  };

  if (format.kind === 'email') {
    return {
      ...base,
      layoutMode: 'VERTICAL',
      layoutSizingHorizontal: 'FIXED',
      layoutSizingVertical: 'HUG',
      layoutPaddingTop: 0,
      layoutPaddingBottom: 0,
      layoutPaddingLeft: 0,
      layoutPaddingRight: 0,
      pluginData: { isArtboard: true, minHeight: format.minHeight ?? EMAIL_MIN_HEIGHT },
    };
  }
  return base;
}

/** A fresh single-artboard document for the given format. */
export function createArtboardDoc(format: ArtboardFormat): SceneDocument {
  const id = generateNodeId();
  const frame = makeArtboardFrame(format, id);
  return {
    version: 2,
    width: format.width,
    height: format.height,
    nodes: { [id]: frame },
    rootChildren: [id],
  } as SceneDocument;
}

/**
 * Upgrade a legacy document (loose background rectangle + content at the root)
 * into the artboard model, preserving size and content. No-op when:
 *   - the doc is empty,
 *   - the roots are workspace canvas frames (`frameCanvasId` set) — never wrap
 *     the multi-frame workspace doc.
 *
 * Without a format hint, upgraded artboards are FIXED at the current doc size.
 * With `{ format: 'email' }` (passed by email entry points), the artboard hugs
 * its content bounds instead: layoutMode stays NONE so absolutely-positioned
 * legacy/AI content keeps its layout, but layoutSizingVertical 'HUG' makes the
 * height track content (floor at minHeight). The doc's current height is
 * preserved on first open via layoutPaddingBottom, so nothing visually jumps.
 * A single-FRAME-root doc that was previously upgraded to FIXED is healed to
 * hugging the same way. Content is kept visible (no clip) to avoid hiding
 * anything that overflowed before.
 */
export function ensureArtboardRoot(
  doc: SceneDocument,
  opts?: { format?: 'email' },
): SceneDocument {
  const roots = doc.rootChildren;
  if (!roots || roots.length === 0) return doc;

  // Workspace doc: roots are per-canvas frames. Leave untouched.
  if (roots.some((id) => doc.nodes[id]?.frameCanvasId)) return doc;

  // A link-zone group is an OVERLAY, not artwork: it is the authored click map
  // that the compiler slices the render along, and CanvasLinkSlicer only ever
  // finds it as a ROOT child. Folding it into the artboard frame like a stray
  // (which is what it looks like to the branches below) silently unpublishes
  // every zone -- the Links panel goes blank and, once the folded doc is saved
  // back, the compiled email loses its links altogether. Set the zone roots
  // aside, upgrade the artwork, then restore them beside the artboard frame.
  const zoneRoots = roots.filter((id) => isLinkZoneRoot(doc.nodes[id]));
  if (zoneRoots.length > 0) {
    const artworkRoots = roots.filter((id) => !isLinkZoneRoot(doc.nodes[id]));
    const upgraded = ensureArtboardRoot({ ...doc, rootChildren: artworkRoots }, opts);
    return { ...upgraded, rootChildren: [...upgraded.rootChildren, ...zoneRoots] };
  }

  // Already a single artboard frame.
  if (roots.length === 1) {
    const only = doc.nodes[roots[0]];
    if (only?.type === 'FRAME') {
      if (opts?.format === 'email' && only.layoutSizingVertical !== 'HUG') {
        return { ...doc, nodes: { ...doc.nodes, [only.id]: healEmailArtboard(only, doc) } };
      }
      return doc;
    }
  }

  // One artboard FRAME root with loose siblings (content added before
  // root-level adds defaulted into the page frame): fold the strays into the
  // artboard instead of wrapping everything in a second frame, then re-run
  // for the single-root heal.
  const artboardRoots = roots.filter(
    (id) =>
      doc.nodes[id]?.type === 'FRAME' &&
      doc.nodes[id]?.pluginData?.['isArtboard'] === true,
  );
  if (artboardRoots.length === 1 && roots.length > 1) {
    const frame = doc.nodes[artboardRoots[0]];
    const strays = roots.filter((id) => id !== frame.id);
    const nextNodes: Record<string, SceneNode> = { ...doc.nodes };
    const children = [...(frame.children ?? [])];
    for (const id of strays) {
      const n = nextNodes[id];
      nextNodes[id] = { ...n, parentId: frame.id, x: n.x - frame.x, y: n.y - frame.y };
      children.push(id);
    }
    nextNodes[frame.id] = { ...frame, children };
    return ensureArtboardRoot(
      { ...doc, nodes: nextNodes, rootChildren: [frame.id] },
      opts,
    );
  }

  // Find a background rectangle to fold into the artboard's fill.
  const bgId = roots.find((id) => {
    const n = doc.nodes[id];
    return n?.type === 'RECTANGLE' && n.pluginData?.['isBackground'] === true;
  });
  const bg = bgId ? doc.nodes[bgId] : undefined;
  const fill = bg?.fills?.length ? bg.fills : [WHITE];

  const frameId = generateNodeId();
  const childIds = roots.filter((id) => id !== bgId);

  const nextNodes: Record<string, SceneNode> = {};
  for (const [id, node] of Object.entries(doc.nodes)) {
    if (id === bgId) continue; // drop the loose bg rect
    nextNodes[id] = childIds.includes(id) ? { ...node, parentId: frameId } : node;
  }

  const frame: SceneNode = {
    id: frameId,
    type: 'FRAME',
    name: opts?.format === 'email' ? 'Email' : 'Artboard',
    parentId: null,
    x: 0,
    y: 0,
    width: doc.width,
    height: doc.height,
    fills: fill,
    clipContent: false,
    children: childIds,
    pluginData: { isArtboard: true },
  };

  nextNodes[frameId] = opts?.format === 'email'
    ? withEmailHug(frame, contentBottom(nextNodes, childIds), doc.height)
    : frame;

  return { ...doc, nodes: nextNodes, rootChildren: [frameId] };
}

/**
 * Make a frame hug its content vertically while preserving `currentHeight` on
 * this pass: minHeight floors short docs at their current height (capped at
 * EMAIL_MIN_HEIGHT) and bottom padding keeps any existing trailing whitespace.
 * layoutMode is left as-is (NONE for upgraded docs) so absolute positions hold.
 */
function withEmailHug(
  frame: SceneNode,
  bottom: number,
  currentHeight: number,
): SceneNode {
  return {
    ...frame,
    layoutSizingHorizontal: 'FIXED',
    layoutSizingVertical: 'HUG',
    layoutPaddingBottom: Math.max(0, Math.round(currentHeight - bottom)),
    pluginData: {
      ...frame.pluginData,
      isArtboard: true,
      minHeight: Math.min(EMAIL_MIN_HEIGHT, Math.round(currentHeight)),
    },
  };
}

/** Heal a previously upgraded FIXED artboard root into a hugging email artboard. */
function healEmailArtboard(frame: SceneNode, doc: SceneDocument): SceneNode {
  const bottom = contentBottom(doc.nodes, frame.children ?? []);
  return withEmailHug(frame, bottom, frame.height);
}

/**
 * Bottom edge of the lowest measurable child (parent coords, AABB-rotated).
 * Mirrors the hug engine's exclusions (scene-layout hugNoneContainer) so the
 * preserved-height padding computed here reproduces the doc height exactly.
 */
function contentBottom(
  nodes: Record<string, SceneNode>,
  childIds: string[],
): number {
  let bottom = 0;
  for (const id of childIds) {
    const n = nodes[id];
    if (!n || n.visible === false) continue;
    if (n.slotRole === 'link-zone') continue;
    if (
      n.pluginData?.['isBackground'] === true ||
      n.constraintVertical === 'TOP_BOTTOM' ||
      n.slotRole === 'background' ||
      n.slotRole === 'background-image'
    ) {
      continue;
    }
    const rotation = n.rotation ?? 0;
    if (!rotation) {
      bottom = Math.max(bottom, n.y + n.height);
      continue;
    }
    const rad = (rotation * Math.PI) / 180;
    const extentY =
      Math.abs((n.width / 2) * Math.sin(rad)) + Math.abs((n.height / 2) * Math.cos(rad));
    bottom = Math.max(bottom, n.y + n.height / 2 + extentY);
  }
  return bottom;
}
